import {groupByUniqueKey} from '@benchling/js-utils';
import assert from 'assert';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {OUTPUT_NODE_CLIENT_ID} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {getClientIdForProcessFlowchartEdge} from 'processes/graph-builder-processes/flowchart-viz/util/ConvertProcessFlowchartForReactFlowUtil';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  getAdjacencyMapFromProcessFlowchart,
  getNodesByClientId,
  getOutboundEdges,
  getSchemaIdFromNodeClientId,
  getSchemaIdMappingToNode,
  getTaskNodesDownstreamOfNode,
  getTaskNodesUpstreamOfNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphGetters';
import {getSchemasReferencedInRoutingRules} from 'processes/modals/router-config/RouterConfigUtils';
import {
  ProcessFlowchartEdgeType,
  TransitionSource,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import APIUtil from 'util/APIUtil';
import {getAllPathsBetweenNodes} from 'util/graph-utils/getAllPathsBetweenNodes';

import {isRouterChained} from '../flowchart-viz/util/flowchartHasChainedRouters';
import {
  ProcessFlowchartErrorType,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
  MaybeValidationError,
  DataFlowEdgeForValidation,
} from './ProcessFlowchartForValidationTypes';
import {
  ProcessEdgeForValidation,
  ProcessRouterNodeForValidation,
  ProcessNodeForValidation,
  ProcessTaskSchemaNodeForValidation,
  ProcessFlowchartForValidation,
} from './ProcessFlowchartForValidationTypes';
import {getDisplayNameForNode} from './validateProcessFlowchartRules';

const CHAINED_ROUTER_LIMIT = 5;

export function ensureRouterNodeHasOneIncomingEdge(
  node: ProcessRouterNodeForValidation,
  edges: Array<ProcessEdgeForValidation>
): MaybeValidationError {
  const numIncomingEdges = edges.filter((edge) => edge.target === node.id).length;
  if (numIncomingEdges !== 1) {
    return {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: 'Connect this router to one source.',
      errorType: ProcessFlowchartErrorType.ROUTER_HAS_MULTIPLE_SOURCE_NODES,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureRouterNodeHasTwoOutgoingEdges(
  node: ProcessRouterNodeForValidation,
  edges: Array<ProcessEdgeForValidation>
): MaybeValidationError {
  const numOutgoingEdges = edges.filter((edge) => edge.source === node.id).length;
  if (numOutgoingEdges < 2) {
    return {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `Connect to at least ${2 - numOutgoingEdges} more ${pluralize(
        numOutgoingEdges,
        'destination'
      )} to configure this router.`,
      errorType: ProcessFlowchartErrorType.ROUTER_MISSING_OUTGOING_EDGES,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureRouterNodeHasRouteConfiguredForEachEdge(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const error = {
    object: {
      id: node.id,
      type: ProcessFlowchartObjectType.NODE,
    },
    errorMessage: `Configure a rule for every path coming out of this router.`,
    errorType: ProcessFlowchartErrorType.ROUTER_EDGES_NOT_CONFIGURED,
    blocksPersistence: true,
  };
  const routerRoutes = [...node.routerConfig.functionSpecs, node.routerConfig.defaultFunctionSpec];
  const routeEdgeIds = _.compact(
    routerRoutes.map((route) => {
      if (route == null || !route.isConfigured) {
        return null;
      }
      return (
        route.edgeConfigReference?.clientId ??
        getClientIdForProcessFlowchartEdge(node.id, OUTPUT_NODE_CLIENT_ID)
      );
    })
  );
  const outputEdges = flowchart.edges.filter(({source}) => source === node.id);
  for (const edge of outputEdges) {
    if (
      !routeEdgeIds.includes(edge.id) &&
      !routeEdgeIds.includes(getClientIdForProcessFlowchartEdge(edge.source, edge.target))
    ) {
      return error;
    }
  }
  // Every route is configured
  if (!routerRoutes.every((route) => route?.isConfigured)) {
    return error;
  }
  return null;
}

export function ensureRouterNodesNotChained(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const outboundEdges = flowchart.edges.filter(({source}) => source === node.id);
  const areAnyDestinationNodesRouters = outboundEdges
    .map(({target}) => flowchart.nodes.find(({id}) => id === target))
    .some((node) => node?.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS);
  if (areAnyDestinationNodesRouters) {
    return {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `This router node cannot have a path to another router node.`,
      errorType: ProcessFlowchartErrorType.ROUTER_CONNECTS_TO_ANOTHER_ROUTER,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureChainedRouterLimit(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const error = {
    object: {
      id: node.id,
      type: ProcessFlowchartObjectType.NODE,
    },
    errorMessage: `There cannot be a path with more than five consecutive routers.`,
    errorType: ProcessFlowchartErrorType.CHAINED_ROUTER_LIMIT_EXCEEDED,
    blocksPersistence: true,
  };
  const visited = new Set();
  /**  For each router (key), routerChainedLength contains an associated chained length (value) */
  const routerChainedLength = new Map<ProcessRouterNodeForValidation, number>([[node, 1]]);
  const queue: Array<ProcessRouterNodeForValidation> = [];
  queue.push(node);
  while (queue.length) {
    const currentRouter = queue.shift()!;
    if (
      currentRouter.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
      !visited.has(currentRouter)
    ) {
      visited.add(currentRouter);
      const incomingEdges = flowchart.edges.filter(({target}) => target === currentRouter.id);
      const parentNodes = incomingEdges
        .map(({source}) => flowchart.nodes.find(({id}) => id === source))
        .filter(
          (node: ProcessNodeForValidation | undefined): node is ProcessNodeForValidation => node !== undefined
        );
      const parentRouterNodes = parentNodes.filter(
        ({nodeType}) => nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
      ) as Array<ProcessRouterNodeForValidation>;
      for (const router of parentRouterNodes) {
        const currentRouterChainedLength = routerChainedLength.get(currentRouter);
        assert(
          currentRouterChainedLength !== undefined,
          `Chained length for ${currentRouter.name} is undefined.`
        );
        const chainedLength = currentRouterChainedLength + 1;
        if (chainedLength > CHAINED_ROUTER_LIMIT) {
          return error;
        }
        routerChainedLength.set(router, chainedLength);
      }
      queue.push(...parentRouterNodes);
    }
  }
  return null;
}

export function ensureNoChainedRouters(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  if (isRouterChained(node, flowchart)) {
    return {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `Benchling now supports multiple conditions in a router.\nReplace chained routers with a single router that contains multiple conditions or condition groups.`,
      errorType: ProcessFlowchartErrorType.CHAINED_ROUTERS_NOT_SUPPORTED,
      blocksPersistence: true,
    };
  }
  return null;
}

export function ensureRouterNodeDestinationNodesAreNotSameSchema(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const childSchemaIds = new Set();
  const visited = new Set();
  const queue: Array<ProcessNodeForValidation> = [];
  queue.push(node);
  while (queue.length) {
    const currentNode = queue.shift()!;
    if (
      currentNode.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
      !visited.has(currentNode)
    ) {
      visited.add(currentNode);
      const outgoingEdges = flowchart.edges.filter(({source}) => source === currentNode.id);
      const childNodes = outgoingEdges
        .map(({target}) => flowchart.nodes.find(({id}) => id === target))
        .filter(
          (node: ProcessNodeForValidation | undefined): node is ProcessNodeForValidation => node !== undefined
        );
      queue.push(...childNodes);
    } else if (
      currentNode.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS ||
      currentNode.nodeType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    ) {
      const currSchemaId = getSchemaIdFromNodeClientId(flowchart, currentNode.id);
      if (childSchemaIds.has(currSchemaId)) {
        return {
          object: {
            id: node.id,
            type: ProcessFlowchartObjectType.NODE,
          },
          errorMessage: `Routers can't have multiple tasks with the same schema as destinations.`,
          errorType: ProcessFlowchartErrorType.ROUTER_HAS_MULTIPLE_PATHS_TO_SAME_SCHEMA_NODES,
          blocksPersistence: true,
        };
      }
      childSchemaIds.add(currSchemaId);
    }
  }
  return null;
}

interface EnsureRouterNodeHasOneSourceSchemaParams {
  node: ProcessRouterNodeForValidation;
  flowchart: ProcessFlowchartForValidation;
  designSpace: ProcessFlowchartDesignSpace;
  usingFlowchartFirstConfig: boolean;
}

export function ensureRouterNodeHasOneSourceSchema(
  params: EnsureRouterNodeHasOneSourceSchemaParams
): Array<ProcessFlowchartValidationError> | null {
  const {node, flowchart, designSpace, usingFlowchartFirstConfig} = params;

  const upstreamNodes = getTaskNodesUpstreamOfNode(node.id, flowchart);
  if (upstreamNodes.length !== 1) {
    // A different error will be returned if router is connected to 0 or >1 upstream nodes
    return null;
  }
  const upstreamNode = upstreamNodes[0];
  const downstreamNodes = getTaskNodesDownstreamOfNode(node.id, flowchart);
  const schemaMapperConfigs = designSpace.flowchartConfig.schemaMapperConfigs;
  const mapperConfigByClientId = groupByUniqueKey(schemaMapperConfigs, ({clientId}) => clientId);
  const sourceSchemaIds = new Set<string>();
  // For each downstream node, get the schema IDs mapping to that node
  for (const downstreamNode of downstreamNodes) {
    const schemaIdMappingToNode = getSchemaIdMappingToNode(
      downstreamNode,
      upstreamNode,
      schemaMapperConfigs,
      mapperConfigByClientId
    );
    if (schemaIdMappingToNode != null) {
      sourceSchemaIds.add(schemaIdMappingToNode);
    }
  }
  const downstreamNodeIds = downstreamNodes.map(({id}) => id);
  const differentSourceTypesErrors = downstreamNodes.map((node) => ({
    object: {
      id: node.id,
      type: ProcessFlowchartObjectType.NODE,
    },
    errorMessage: `The transition behaviors from ${upstreamNode.name} through a router must all use the same option. Update this ${getDisplayNameForNode(node, usingFlowchartFirstConfig)}’s transition behavior to match (outputs or tasks).`,
    errorType: ProcessFlowchartErrorType.MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCE_TYPES,
    blocksPersistence: true,
  }));

  const schemaIdsReferencedInRouterRules = getSchemasReferencedInRoutingRules(node.routerConfig);
  if (schemaIdsReferencedInRouterRules.length > 1) {
    return differentSourceTypesErrors;
  }

  const dataFlowEdgesThroughRouter = flowchart.edges.filter(
    ({type, source, target}) =>
      type === ProcessFlowchartEdgeType.DATA_FLOW &&
      source === upstreamNode.id &&
      downstreamNodeIds.includes(target)
  ) as Array<DataFlowEdgeForValidation>;

  const hasDifferentSourceTypes = usingFlowchartFirstConfig
    ? dataFlowEdgesThroughRouter.length > 0 &&
      dataFlowEdgesThroughRouter.some(
        (edge) => edge.transitionSource !== dataFlowEdgesThroughRouter[0].transitionSource
      )
    : sourceSchemaIds.size > 1;

  if (hasDifferentSourceTypes) {
    return differentSourceTypesErrors;
  }

  const differentSourcesThanRouterRulesError = (source: string): Array<ProcessFlowchartValidationError> => [
    {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: `Router rules must use the same fields as the transition behaviors (outputs or tasks). Update this router's rules to use ${upstreamNode.name}'s ${source} fields.`,
      errorType: ProcessFlowchartErrorType.MAPPERS_THROUGH_ROUTER_HAVE_DIFFERENT_SOURCES_THAN_ROUTER_RULES,
      blocksPersistence: true,
    },
  ];

  if (
    usingFlowchartFirstConfig &&
    dataFlowEdgesThroughRouter.length > 0 &&
    schemaIdsReferencedInRouterRules.length === 1
  ) {
    const dataFlowSourceType = dataFlowEdgesThroughRouter[0].transitionSource;
    const sourceType = dataFlowSourceType === TransitionSource.OUTPUTS ? 'output' : 'task';
    const schemaIdReferencedInRouterRules = schemaIdsReferencedInRouterRules[0];
    if (
      (dataFlowSourceType === TransitionSource.OUTPUTS &&
        !APIUtil.isProcessOutputSchema(schemaIdReferencedInRouterRules)) ||
      (dataFlowSourceType === TransitionSource.INPUTS &&
        !APIUtil.isProcessTaskSchema(schemaIdReferencedInRouterRules))
    ) {
      return differentSourcesThanRouterRulesError(sourceType);
    }
  } else if (sourceSchemaIds.size === 1 && schemaIdsReferencedInRouterRules.length === 1) {
    if ([...sourceSchemaIds][0] !== schemaIdsReferencedInRouterRules[0]) {
      const sourceType = APIUtil.isProcessOutputSchema(schemaIdsReferencedInRouterRules[0])
        ? 'output'
        : 'task';
      return differentSourcesThanRouterRulesError(sourceType);
    }
  }

  return null;
}

export function ensureRouterNodeConnectedToNonEndNode(
  node: ProcessRouterNodeForValidation,
  flowchart: ProcessFlowchartForValidation
): MaybeValidationError {
  const outboundEdges = getOutboundEdges(node, flowchart.edges);
  if (outboundEdges.length < 2) {
    // Another error will handle routers that don't have enough outgoing edges
    return null;
  }
  const areAllDestinationNodesEndNodes = outboundEdges
    .map(({target}) => flowchart.nodes.find(({id}) => id === target))
    .every((node) => node == null || node.nodeType === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS);
  if (areAllDestinationNodesEndNodes) {
    return {
      object: {
        id: node.id,
        type: ProcessFlowchartObjectType.NODE,
      },
      errorMessage: "Routers can't have only stops as destinations.",
      errorType: ProcessFlowchartErrorType.ROUTER_ONLY_CONNECTED_TO_END_NODES,
      blocksPersistence: true,
    };
  }
  return null;
}

interface EnsureNoMultipleRouterOnlyPathsBetweenTaskNodesParams {
  destinationNode: ProcessTaskSchemaNodeForValidation;
  flowchart: ProcessFlowchartForValidation;
  usingFlowchartFirstConfig: boolean;
}

export function ensureNoMultipleRouterOnlyPathsBetweenTaskNodes(
  params: EnsureNoMultipleRouterOnlyPathsBetweenTaskNodesParams
): Array<ProcessFlowchartValidationError> | null {
  const {destinationNode, flowchart, usingFlowchartFirstConfig} = params;
  const errors: Array<ProcessFlowchartValidationError> = [];
  const taskNodes = getTaskNodesUpstreamOfNode(destinationNode.id, flowchart);
  const nodesById = getNodesByClientId(flowchart);
  for (const fromNode of taskNodes) {
    const adjacencyMap = getAdjacencyMapFromProcessFlowchart(flowchart);
    const paths = getAllPathsBetweenNodes(fromNode.id, destinationNode.id, adjacencyMap);
    let pathsWithOnlyRouters = 0;
    for (const path of paths) {
      const doesPathContainOnlyRouters = path.every(
        (nodeId) => nodesById[nodeId].nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS
      );
      if (doesPathContainOnlyRouters) {
        pathsWithOnlyRouters++;
      }
    }
    if (pathsWithOnlyRouters > 1) {
      errors.push({
        object: {
          id: destinationNode.id,
          type: ProcessFlowchartObjectType.NODE,
        },
        errorMessage: `This ${getDisplayNameForNode(destinationNode, usingFlowchartFirstConfig)} has duplicate paths from ${fromNode.name} through routers.`,
        errorType: ProcessFlowchartErrorType.DUPLICATE_PATHS_WITH_ROUTER,
        blocksPersistence: true,
      });
    }
  }
  return errors.length > 0 ? errors : null;
}
